import React, { useState } from "react";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react"; // Ensure you're using Headless UI or a similar library
import { Link, useNavigate } from "react-router-dom";
import { recipientIdAtom, showIndividualMessageBoxAtom, showMessageBoxAtom, userAtom } from "../../store";
import { useAtom } from "jotai";
import { useAuth } from "../../context/authContext";
import DialogConfirmation from "../dialogs/DialogConfirmation";

const menuItems = [
  {
    title: 'Home',
    link: '/'
  },
  {
    title: 'Find Cards',
    link: '/marketplace/categories'
  },
  {
    title: 'My Account',
    link: '/my-profile'
  },
  {
    title: 'Create Listing',
    link: '/marketplace/create-listing'
  }, {
    title: 'Support',
    link: '/support'
  },
  {
    title: 'FAQs',
    link: '/faqs'
  }
]

const DropdownMenu = () => {
  const [searchText, setSearchText] = useState('')
  const navigate = useNavigate()
  const [userData, setUserData] = useAtom(userAtom)
  const [showIndividualMessageBox, setShowIndividualMessageBox] = useAtom(showMessageBoxAtom)
  const [recipientId, setRecipientId] = useAtom(recipientIdAtom)
  const [showMembershipRequiredModal, setShowMembershipRequiredModal] = useState(false)
  const { logout } = useAuth()

  const onChangeSearch = (e) => {
    if (e.key === 'Enter') {
      navigate(`/marketplace/categories?search=${searchText}`)
    }
  }

  const setMessageToSeller = () => {
    setShowIndividualMessageBox(true)
  }

  const handleCreateListing = () => {
    if (userData.membership === 'free') {
      setShowMembershipRequiredModal(true)
    } else {
      navigate(`/marketplace/create-listing`)
    }
  }

  return (
    userData &&
    <>
      <DialogConfirmation
        message="Selling requires a membership. Buying is free -- you only need an account."
        open={showMembershipRequiredModal}
        onClose={() => { setShowMembershipRequiredModal(false) }}
        buttonText="View Plans"
        title="Seller Membership Required"
        iconSrc="/assets/logos/logo.svg"
        iconClassName="w-[160px]"
        onButtonClick={() => { navigate('/my-profile'), setShowMembershipRequiredModal(false) }}
        type="confirm"

      />
      <Menu>
        {/* MenuButton already renders a <button>; a nested one here made it
            invalid DOM (button inside button) and swallowed the outer
            button's click handling. */}
        <MenuButton className="inline-flex items-center gap-2 rounded-md bg-transparent py-1.5 text-sm/6 font-semibold focus:outline-none">
          <img src="/assets/icons/icon-menu.svg" alt="Menu" />
        </MenuButton>

        <MenuItems
          transition
          anchor="bottom end"
          className="w-full h-full origin-top-right !top-[68] text-white border shadow-[2px_19px_8px_rgba(97,51,142,.01),1px_11px_7px_rgba(97,51,142,.02),0px_5px_5px_rgba(97,51,142,.03),0px_1px_3px_rgba(97,51,142,.04)] border-white/5 p-2 text-sm/6 transition duration-100 [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:top-[-999px] z-10 bg-[#141414]"
        >
          <div className="flex flex-col container mx-auto px-4">
            <div className="gradient-border mb-8" />

            {menuItems.map((item, index) => (
              <MenuItem key={index}>
                {
                  item.title === 'Create Listing' ?
                    <button className="group flex justify-between w-full items-center gap-2 !border-0 hover:!border-1 py-2.5 hover:text-primary text-white text-base lg:text-lg" onClick={handleCreateListing}>
                      {
                        item.title
                      }
                    </button>
                    :
                    <Link to={item.link}>
                      <button className="group flex justify-between w-full items-center gap-2 !border-0 hover:!border-1 py-2.5 hover:text-primary text-white text-base lg:text-lg">
                        {item.title}
                      </button>
                    </Link>
                }
              </MenuItem>
            ))}

            {
              !userData ?
                <Link className="flex items-center mx-auto mt-20" to='/login'>
                  <img src='/assets/icons/icon-user-default.svg' alt="user" className="mt-1 mr-2" />
                  <span className="text-primary text-lg">Login</span>
                </Link> :
                <button className="flex items-center mx-auto mt-20" onClick={logout}>
                  <img src='/assets/icons/icon-user-default.svg' alt="user" className="mt-1 mr-2" />
                  <span className="text-primary text-lg">Logout</span>
                </button>
            }

            <div className="gradient-border my-6" />

            {
              userData &&
              <button className="flex bg-primary w-full py-3.5 rounded-[4px] justify-center items-center" onClick={setMessageToSeller}>
                <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2">
                  <mask id="mask0_772_1535" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="23" height="24">
                    <rect x="0.0332031" y="0.812561" width="22.3284" height="22.3284" fill="white" />
                  </mask>
                  <g mask="url(#mask0_772_1535)">
                    <path d="M7.47727 11.094C7.70505 11.094 7.89918 11.0138 8.05967 10.8535C8.22 10.693 8.30016 10.4989 8.30016 10.2711C8.30016 10.0433 8.22 9.84919 8.05967 9.6887C7.89918 9.52837 7.70505 9.44821 7.47727 9.44821C7.24949 9.44821 7.05535 9.52837 6.89487 9.6887C6.73454 9.84919 6.65437 10.0433 6.65437 10.2711C6.65437 10.4989 6.73454 10.693 6.89487 10.8535C7.05535 11.0138 7.24949 11.094 7.47727 11.094ZM11.1987 11.094C11.4264 11.094 11.6206 11.0138 11.7811 10.8535C11.9414 10.693 12.0216 10.4989 12.0216 10.2711C12.0216 10.0433 11.9414 9.84919 11.7811 9.6887C11.6206 9.52837 11.4264 9.44821 11.1987 9.44821C10.9709 9.44821 10.7767 9.52837 10.6163 9.6887C10.4559 9.84919 10.3758 10.0433 10.3758 10.2711C10.3758 10.4989 10.4559 10.693 10.6163 10.8535C10.7767 11.0138 10.9709 11.094 11.1987 11.094ZM14.9201 11.094C15.1478 11.094 15.342 11.0138 15.5025 10.8535C15.6628 10.693 15.7429 10.4989 15.7429 10.2711C15.7429 10.0433 15.6628 9.84919 15.5025 9.6887C15.342 9.52837 15.1478 9.44821 14.9201 9.44821C14.6923 9.44821 14.4981 9.52837 14.3377 9.6887C14.1773 9.84919 14.0972 10.0433 14.0972 10.2711C14.0972 10.4989 14.1773 10.693 14.3377 10.8535C14.4981 11.0138 14.6923 11.094 14.9201 11.094ZM2.36035 20.5407V4.97532C2.36035 4.50534 2.52316 4.10754 2.84878 3.78192C3.17441 3.4563 3.57221 3.29349 4.04219 3.29349H18.3551C18.8251 3.29349 19.2229 3.4563 19.5485 3.78192C19.8742 4.10754 20.037 4.50534 20.037 4.97532V15.5669C20.037 16.0369 19.8742 16.4347 19.5485 16.7603C19.2229 17.0859 18.8251 17.2487 18.3551 17.2487H5.65239L2.36035 20.5407ZM5.05836 15.8532H18.3551C18.4268 15.8532 18.4924 15.8233 18.5519 15.7636C18.6116 15.7041 18.6414 15.6385 18.6414 15.5669V4.97532C18.6414 4.90369 18.6116 4.8381 18.5519 4.77856C18.4924 4.71886 18.4268 4.68901 18.3551 4.68901H4.04219C3.97055 4.68901 3.90496 4.71886 3.84542 4.77856C3.78572 4.8381 3.75587 4.90369 3.75587 4.97532V17.1415L5.05836 15.8532Z" fill="white" />
                  </g>
                </svg>

                CHATS ({userData?.unReadMessages?.length || 0})
              </button>
            }

            <div className="text-xs mt-14 flex lg:hidden items-center justify-center 2xl:text-base leading-[1.8] w-full">
              <div className="mr-4">
                <div className="border-style-decoration bg-[#2A2A2A] p-4 w-fit cursor-pointer">
                  <img
                    alt="instagram"
                    src="/assets/logos/instagram.svg"
                    className="max-w-[28px] lg:max-w-[unset]"
                  />
                </div>
              </div>
              <div className="border-style-decoration bg-[#2A2A2A] p-4 w-fit cursor-pointer mr-4">
                <img
                  alt="facebook"
                  src="/assets/logos/facebook.svg"
                  className="max-w-[28px] lg:max-w-[unset]"
                />
              </div>
              <div className="border-style-decoration bg-[#2A2A2A] p-4 w-fit cursor-pointer">
                <img
                  alt="x"
                  src="/assets/logos/x.svg"
                  className="max-w-[28px] lg:max-w-[unset]"
                />
              </div>
            </div>

            <div className="my-8 mx-auto">
              <img src="/assets/logos/logo.svg" className="w-[112px]" alt="logo" />
            </div>
          </div>
        </MenuItems>
      </Menu>
    </>
  );
};

export default DropdownMenu;
